import { RocheInputAdornment } from "@one/react-kit";
import styled from "styled-components";
import React from "react";
import { OwcIconButton } from "@one/react";
import { OPENSEARCH } from "../../constants";
import { DATA_MODEL_TABLE, getFieldValue } from "@digitallab/grid-common-frontend-components";
import { convertToSnakeCase } from "../../utils/helpers/text";

export const Input = styled.div`
  margin-bottom: 20px;
  margin-right: -5px;
`;

export const hasFormikObjectError = (formik, fieldName) => {
  return (
    (formik.touched[fieldName]?.value || formik.touched[fieldName] === true) &&
    Boolean(formik.errors[fieldName]?.value ?? formik.errors[fieldName])
  );
};
export const getFormikObjectError = (formik, fieldName) => {
  return (
    (formik.touched[fieldName]?.value || formik.touched[fieldName] === true) &&
    (formik.errors[fieldName]?.value ?? formik.errors[fieldName])
  );
};

const commonPropsForInputs =
  ({
    fieldKeyFn = (key) => `${key}.value`,
    valueFn = (formik, key) => formik.values?.[key]?.value,
    errorFn = (formik, key) => hasFormikObjectError(formik, key),
    helperTextFn = (formik, key) => getFormikObjectError(formik, key),
  } = {}) =>
    (
      classes,
      formik,
      key,
      dataTestIdKey,
      dataTestIdForFormKey,
      isEdit = false,
      showBadge = false,
      isUpdateBadge = false
    ) => ({
      InputProps: {
        endAdornment: (
          <RocheInputAdornment position="end">
            {showBadge && (
              <>
                <i
                  className="one-icons"
                  style={{
                    position: "absolute",
                    color: isUpdateBadge === true ? "#00875A" : "#737373",
                    fontSize: 11,
                    right: 11,
                    top: 5,
                    "z-index": "2",
                  }}
                >
                  certificate
                </i>
              </>
            )}
            {valueFn(formik, key)?.length > 0 && !isEdit && (
              <>
                <OwcIconButton
                  data-testid={`${dataTestIdForFormKey}-clear-button-${dataTestIdKey}-input`}
                  edge="end"
                  onClick={() => formik.setFieldValue(fieldKeyFn(key), "", true)}
                  icon="circle_clear"
                  type="legacy"
                  flat
                />
              </>
            )}
          </RocheInputAdornment>
        ),
      },
      error: errorFn(formik, key),
      helperText: helperTextFn(formik, key),
      FormHelperTextProps: {
        "data-testid": `${dataTestIdForFormKey}-helper-text-${dataTestIdKey}-input`,
      },
    });

export const commonPropsForInputsWithValue = commonPropsForInputs();
export const commonPropsForInputsWithoutValue = commonPropsForInputs({
  fieldKeyFn: (key) => key,
  valueFn: (formik, key) => formik.values?.[key],
  errorFn: (formik, key) => formik.touched[key] && Boolean(formik.errors[key]),
  helperTextFn: (formik, key) => formik.touched[key] && formik.errors[key],
});

export const changeDateFormat = (inputDate, format = null) => {
  try {
    if (!inputDate) {
      return null;
    }
    if (typeof inputDate === "string") {
      if (format === null) {
        const dateObj = new Date(inputDate).toUTCString();
        const splitDateFormat = dateObj?.split(" ");
        const currentDate = splitDateFormat[1] + "-" + splitDateFormat[2] + "-" + splitDateFormat[3];
        const FormattedDateObj = new Date(currentDate);
        return FormattedDateObj;
      }
      if (format === "yyyy-MM-DD") {
        return inputDate;
      }
    } else {
      const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const convertedDate = inputDate.toLocaleString("en-GB", {
        timeZone: localTimeZone,
      });

      const splitDate = convertedDate?.split(",");
      if (splitDate.count === 0) {
        throw new Error("No valid date found for conversion");
      }

      const splitDateFormat = splitDate[0]?.split("/");
      if (splitDate.count === 0) {
        throw new Error("No valid date found for conversion");
      }

      var day = splitDateFormat[0];
      var month = splitDateFormat[1];
      var year = splitDateFormat[2];

      return year + "-" + month + "-" + day;
    }
  } catch (error) {
    console.log(error);
  }
};

export const changeStringToFormattedDate = (inputDate, format) => {
  const dateObj = new Date(inputDate).toUTCString();
  const splitDateFormat = dateObj?.split(" ");
  if (format === "dd-MMM-yyyy") {
    return splitDateFormat[1] + "-" + splitDateFormat[2] + "-" + splitDateFormat[3];
  }
  if (format === "dd-MMM-yyyy, HH:mm") {
    const hrMinute = splitDateFormat[4].split(":");
    return (
      splitDateFormat[1] + "-" + splitDateFormat[2] + "-" + splitDateFormat[3] + ", " + hrMinute[0] + ":" + hrMinute[1]
    );
  }
};

export const OPENSEARCH_CONFIGURATION = process.env.REACT_APP_SEARCH_ENGINE?.toUpperCase() === OPENSEARCH;
export { getFieldValue };

export const normalizeBooleanFilters = (query) => {
  if (!query || typeof query !== 'string') {
    return query;
  }
  const isHazardousName = convertToSnakeCase(DATA_MODEL_TABLE.isHazardous.key);
  const shouldAssistedName = convertToSnakeCase(DATA_MODEL_TABLE.shouldAssisted.key);
  const stringValuesRegex = new RegExp(`(${isHazardousName}|${shouldAssistedName}): ?"?(Yes|No)"?`, "gi");
  const booleanValuesRegex = new RegExp(`(${isHazardousName}|${shouldAssistedName}): ?"?(true|false)"?`, "gi");
  return query
    .replace(stringValuesRegex, (_, match, value) => `${match}:${value.toLowerCase() === 'yes' ? 1 : 0}`)
    .replace(booleanValuesRegex, (_, match, value) => `${match}:${value.toLowerCase() === 'true' ? 1 : 0}`);
};

export const normalizeBookingAccessControlFilters = (query) => {
  if (!query || typeof query !== "string") {
    return query;
  }

  // booking_access_control is not indexed directly in search.
  // Rewrite it to the indexed access-control field used by OpenSearch/CloudSearch.
  return query
    .replace(
      /booking_access_control:\*?"Allowed"\*?/gi,
      '(is_booking_controlled:"false" OR is_booking_controlled:false OR is_booking_controlled:0 OR -is_booking_controlled:*)'
    )
    .replace(
      /booking_access_control:\*?"Restricted"\*?/gi,
      '(is_booking_controlled:"true" OR is_booking_controlled:true OR is_booking_controlled:1)'
    );
};

export const handleBookingFields = (cloudSearchConfigOption) => {
  const normalizedSearchValue = normalizeBookingAccessControlFilters(
    normalizeBooleanFilters(cloudSearchConfigOption.searchValue)
  );
  const normalizedFilterQuery = normalizeBookingAccessControlFilters(
    normalizeBooleanFilters(cloudSearchConfigOption.filterQuery)
  );

  return {
    ...cloudSearchConfigOption,
    searchValue: normalizedSearchValue,
    filterQuery: normalizedFilterQuery
  };
};
